import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { openWhatsApp } from "@/lib/whatsapp";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Search, Copy, MessageCircle, Ban, ArrowLeft, Eye, Loader2 } from "lucide-react";

const walletUrl = (t) => `${window.location.origin}/api/w/${t}`;

const CustomerPicker = ({ onPick }) => {
  const [q, setQ] = useState("");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setList([]); setLoading(false); return; }
    let live = true;
    setLoading(true);
    const t = setTimeout(() => api.get("/customers", { params: { search: q.trim(), page: 1, page_size: 10 } })
      .then((r) => { if (live) setList(r.data.items); }).catch(() => { if (live) setList([]); })
      .finally(() => { if (live) setLoading(false); }), 250);
    return () => { live = false; clearTimeout(t); };
  }, [q]);
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
        <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Müşteri adı, soyadı, TC, telefon veya plaka…" className="pl-9" data-testid="wallet-customer-search" />
        {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-emerald-600" data-testid="wallet-search-loading" />}
      </div>
      <div className="max-h-72 overflow-auto divide-y divide-zinc-100 rounded-md border border-zinc-100">
        {q.trim().length >= 2 && !loading && !list.length && <p className="p-4 text-sm text-zinc-400 text-center" data-testid="wallet-search-empty">Müşteri bulunamadı</p>}
        {list.map((c) => (
          <button key={c.key} onClick={() => onPick({ customer_key: c.key, name: c.name, phone: c.phone })} data-testid={`wallet-pick-${c.key}`}
            className="w-full text-left px-3 py-2.5 hover:bg-emerald-50 transition-colors">
            <p className="text-sm font-medium text-zinc-900">{c.name}</p>
            <p className="text-[11px] text-zinc-400">{[c.tc_no ? `TC ${c.tc_no}` : "TC yok", c.phone, (c.types || []).join(", ") || `${c.active_count} aktif poliçe`].filter(Boolean).join(" · ")}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

const WalletLink = ({ cust, onBack }) => {
  const [w, setW] = useState(undefined);
  const [phone, setPhone] = useState(cust.phone || "");
  const [agency, setAgency] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/wallets", { params: { customer_key: cust.customer_key } }).then((r) => setW(r.data)).catch(() => setW(null));
    api.get("/settings/report").then((r) => setAgency(r.data.company_name || "")).catch(() => {});
  }, [cust.customer_key]);
  const create = async () => {
    setBusy(true);
    try { setW((await api.post("/wallets", { customer_key: cust.customer_key })).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Bağlantı oluşturulamadı"); }
    setBusy(false);
  };
  const revoke = async () => {
    await api.delete(`/wallets/${w.id}`); setW(null); toast.success("Bağlantı kapatıldı, müşteri artık açamaz");
  };
  const msg = () => `Merhaba ${cust.name},\n${agency ? `${agency} olarak ` : ""}tüm poliçelerinizi, belgelerinizi ve vade tarihlerinizi tek yerden görebileceğiniz *dijital poliçe cüzdanınız* hazır:\n${walletUrl(w.token)}`;
  return (
    <div className="space-y-4">
      {!cust.customer_key?.startsWith("group:") && <button onClick={onBack} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900" data-testid="wallet-back"><ArrowLeft className="h-3.5 w-3.5" />Başka müşteri seç</button>}
      <p className="font-heading text-lg font-semibold text-zinc-900" data-testid="wallet-customer-name">{cust.name}</p>
      {w === undefined && <Loader2 className="h-5 w-5 animate-spin text-emerald-600" />}
      {w === null && (
        <Button onClick={create} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="wallet-create-btn">Cüzdan bağlantısı oluştur</Button>
      )}
      {w && (
        <>
          <div className="flex gap-2">
            <Input readOnly value={walletUrl(w.token)} className="font-mono text-xs" data-testid="wallet-link" />
            <Button variant="outline" onClick={() => { navigator.clipboard.writeText(walletUrl(w.token)); toast.success("Bağlantı kopyalandı"); }} data-testid="wallet-copy-btn"><Copy className="h-4 w-4" /></Button>
          </div>
          <p className="text-xs text-zinc-500 inline-flex items-center gap-1.5" data-testid="wallet-views"><Eye className="h-3.5 w-3.5" />Müşteri {w.views || 0} kez açtı</p>
          <div className="flex gap-2">
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Telefon (05xx…)" data-testid="wallet-phone" />
            <Button disabled={!phone} onClick={() => openWhatsApp(phone, msg())} className="bg-[#25D366] hover:bg-[#1ebe5a] text-white gap-1.5 shrink-0" data-testid="wallet-wa-btn"><MessageCircle className="h-4 w-4" />WhatsApp ile gönder</Button>
          </div>
          <Button variant="ghost" onClick={revoke} className="text-rose-600 hover:bg-rose-50 gap-1.5 px-2" data-testid="wallet-revoke-btn"><Ban className="h-4 w-4" />Bağlantıyı kapat</Button>
        </>
      )}
    </div>
  );
};

export const WalletDialog = ({ open, onOpenChange, customer }) => {
  const [cust, setCust] = useState(null);
  useEffect(() => { if (open) setCust(customer || null); }, [open, customer]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="wallet-dialog">
        <DialogHeader>
          <DialogTitle>Dijital Poliçe Cüzdanı</DialogTitle>
          <DialogDescription>Müşteri bu bağlantıdan poliçelerini, PDF'lerini ve vade tarihlerini görür. Bağlantı siz kapatana kadar açık kalır. <a href="/settings?tab=brand" className="text-emerald-700 underline" data-testid="wallet-brand-link">Acente adı ve logonuzu ayarlayın</a></DialogDescription>
        </DialogHeader>
        {cust ? <WalletLink cust={cust} onBack={() => setCust(null)} /> : <CustomerPicker onPick={setCust} />}
      </DialogContent>
    </Dialog>
  );
};
